import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  ButtonSize,
  Modal,
  Button,
  ButtonVariant,
  IconButton,
  Show,
  IconButtonVariant
} from "shared/components";
import {
  Applicant,
  Application,
  MergeApplicationsError
} from "types/application";
import { useNavigate } from "react-router-dom";

import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import { getFullName } from "shared/utils/get-full-name";
import { useCallback, useEffect, useState } from "react";
import { getApplicantRole } from "utils/getApplicantRole";
import { AxiosError } from "axios";
import { Cancel01Icon } from "@hugeicons-pro/core-solid-rounded";
import { getShortAddressFromAddress } from "shared/addressUtils";
import { Invitee } from "shared/types/application";
import { getNonCoSigners } from "../ApplicationsTableView/utils/applicationSplittingUtils";
import { TransferList } from "shared/components/TransferList";
import { Card } from "shared/components/Card";

const getApplication = async (id: string) => {
  return instance<Application>(`/portal/v1/applications/${id}`).then(
    ({ data }) => data
  );
};

export const SplitModal = ({
  isOpen,
  onClose,
  applicationId
}: {
  isOpen: boolean;
  onClose: () => void;
  applicationId: string;
}) => {
  const navigate = useNavigate();
  const [isConfirmStep, setIsConfirmStep] = useState<boolean>(false);
  const [remainingApplicants, setRemainingApplicants] = useState<
    (Applicant | Invitee)[]
  >([]);
  const [movingApplicants, setMovingApplicants] = useState<
    (Applicant | Invitee)[]
  >([]);
  const [originalListErrorMessage, setOriginalListErrorMessage] = useState<
    string | undefined
  >(undefined);
  const [newListErrorMessage, setNewListErrorMessage] = useState<
    string | undefined
  >(undefined);

  const applicationMeta = useQuery({
    queryKey: ["application", String(applicationId)],
    queryFn: () => getApplication(applicationId),
    enabled: Boolean(applicationId)
  });

  useEffect(() => {
    const allUsers = [
      ...(applicationMeta.data?.applicants || []),
      ...(applicationMeta.data?.pending_magic_links || [])
    ];
    setRemainingApplicants(allUsers);
  }, [
    applicationMeta.data?.applicants,
    applicationMeta.data?.pending_magic_links
  ]);

  const getApplicationSplitErrorMessage = useCallback(
    (applicants: (Applicant | Invitee)[]) => {
      if (applicants.length === 0) return "Application can't be empty";
      if (getNonCoSigners(applicants) === 0)
        return "Application can't have only a guarantor";
      return "";
    },
    []
  );

  const resetListErrorMessages = useCallback(() => {
    setOriginalListErrorMessage(undefined);
    setNewListErrorMessage(undefined);
  }, []);

  const handleMoveExistingApplicant = useCallback(
    (applicant: Applicant | Invitee) => {
      setRemainingApplicants((prev) => prev.filter((a) => a !== applicant));
      setMovingApplicants((prev) => [applicant, ...prev]);
      resetListErrorMessages();
    },
    []
  );

  const handleMoveBackApplicant = useCallback(
    (applicant: Applicant | Invitee) => {
      setMovingApplicants((prev) => prev.filter((a) => a !== applicant));
      setRemainingApplicants((prev) => [applicant, ...prev]);
      resetListErrorMessages();
    },
    []
  );

  const getApplicantName = useCallback((applicant: Applicant | Invitee) => {
    if ("first_name" in applicant && "last_name" in applicant)
      return getFullName({
        firstName: applicant.first_name,
        lastName: applicant.last_name,
        middleName: applicant.middle_name,
        short: true
      });
    return applicant.email;
  }, []);

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const moving_applicant_ids = movingApplicants
        .filter((applicant) => "first_name" in applicant)
        .map((applicant) => applicant.id);
      const moving_magic_link_ids = movingApplicants
        .filter((applicant) => !("first_name" in applicant))
        .map((link) => link.id);
      return instance.post<void, { data: Application }>(
        "/portal/v1/applications/split",
        {
          target_application_id: applicationId,
          moving_applicant_ids,
          moving_magic_link_ids
        }
      );
    },
    mutationKey: ["application", applicationId],
    onSuccess: (response: { data: Application }) => {
      toast.success("Application successfully split");
      onClose();
      navigate(`/applications/${response.data.id}`);
    },
    onError: (error: AxiosError<MergeApplicationsError>) => {
      const allErrors = error.response?.data.validation_errors
        .map((el) => el.errors)
        .flat();
      if (allErrors) {
        if (
          allErrors.includes(
            "merging_applications_should_not_have_association_to_at_least_one_customer"
          )
        )
          toast.error(
            "This application already has an active applicant with the same contact details. Please cancel the duplicate applicant before attempting to merge these application groups."
          );
        else if (allErrors.includes("not_mergeable_status"))
          toast.error(
            "The target application must be in 'Pending decision', 'In progress' or 'Submitted' status"
          );
      } else toast.error(getErrorMessage(error));
    }
  });

  const handleSplitApplication = useCallback(() => {
    if (isConfirmStep) {
      if (!isPending) mutate();
    } else {
      const remainingErrorMessage =
        getApplicationSplitErrorMessage(remainingApplicants);
      const movingErrorMessage =
        getApplicationSplitErrorMessage(movingApplicants);
      if (remainingErrorMessage || movingErrorMessage) {
        setOriginalListErrorMessage(remainingErrorMessage);
        setNewListErrorMessage(movingErrorMessage);
        return;
      }
      setIsConfirmStep(true);
    }
  }, [isConfirmStep, isPending, remainingApplicants, movingApplicants, mutate]);

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      panelClassName="max-w-[600px] w-full flex flex-col"
    >
      <div className="p-6 border-b border-alpha-100 flex flex-col gap-4">
        <div className="space-y-1">
          <div className="flex justify-between items-center">
            <h1 className="text-xl text-content-1000 font-bold">
              Split application
            </h1>
            <IconButton
              onClick={onClose}
              variant={IconButtonVariant.BASE}
              borderLess
              HugeIcon={Cancel01Icon}
              strokeWidth={0.25}
            />
          </div>

          <Show if={!isConfirmStep}>
            <p className="text-sm text-content-500 font-normal">
              Select applicants to split into a new application.
            </p>
          </Show>
        </div>

        <div className="border-b border-alpha-40" />

        <div className="space-y-1 text-sm">
          <p className="text-content-1000 font-bold">
            {applicationMeta.data?.property.name}
          </p>

          <p className="text-content-500 text-xs font-normal">
            {getShortAddressFromAddress(applicationMeta.data?.property.address)}
          </p>
        </div>
      </div>

      <Show if={!isConfirmStep}>
        <div className="h-full grow px-6 py-4 bg-background-900 flex">
          <TransferList
            remainingItems={remainingApplicants}
            movingItems={movingApplicants}
            originalListTitle="Current application"
            newListTitle="New application"
            itemDetails={(applicant) => (
              <div className="space-y-2">
                <p className="text-sm text-content-1000 font-medium truncate w-[164px]">
                  {getApplicantName(applicant)}
                </p>
                <p className="text-xs text-content-400">
                  {getApplicantRole(applicant.role)}
                </p>
              </div>
            )}
            getItemId={(item) => {
              if ("first_name" in item) return item.id + "_applicant";
              return item.id + "_invitee";
            }}
            onMoveToNew={handleMoveExistingApplicant}
            onMoveBack={handleMoveBackApplicant}
            originalListErrorMessage={originalListErrorMessage}
            newListErrorMessage={newListErrorMessage}
          />
        </div>
      </Show>

      <Show if={isConfirmStep}>
        <div className="h-full px-6 py-4 bg-background-900 space-y-4 overflow-y-auto">
          <Card className="px-6 py-4 space-y-4">
            <div className="space-y-1">
              <p className="text-sm text-content-400">
                Current application group
              </p>
              <p className="text-base text-content-1000 font-bold">
                {remainingApplicants
                  .map((applicant) => getApplicantName(applicant))
                  .join(", ")}
              </p>
            </div>
            <div className="border-b border-alpha-40" />
            {remainingApplicants.map((applicant, idx) => (
              <div key={idx}>
                <div className="space-y-1">
                  <p className="text-sm text-content-1000 font-medium truncate w-[500px]">
                    {getApplicantName(applicant)}
                  </p>
                  <p className="text-xs text-content-400">
                    {getApplicantRole(applicant.role)}
                  </p>
                </div>
              </div>
            ))}
          </Card>
          <Card className="px-6 py-4 space-y-4">
            <div className="space-y-1">
              <p className="text-sm text-content-400">New application group</p>
              <p className="text-base text-content-1000 font-bold">
                {movingApplicants
                  .map((applicant) => getApplicantName(applicant))
                  .join(", ")}
              </p>
            </div>
            <div className="border-b border-alpha-40" />
            {movingApplicants.map((applicant, idx) => (
              <div key={idx}>
                <div className="space-y-1">
                  <p className="text-sm text-content-1000 font-medium truncate w-[500px]">
                    {getApplicantName(applicant)}
                  </p>
                  <p className="text-xs text-content-400">
                    {getApplicantRole(applicant.role)}
                  </p>
                </div>
              </div>
            ))}
          </Card>
        </div>
      </Show>

      <div className="flex h-[72px] justify-end px-6 py-4 border-t border-alpha-100 gap-2">
        <Button
          size={ButtonSize.SM}
          onClick={() => {
            if (isConfirmStep) setIsConfirmStep(false);
            else onClose();
          }}
        >
          Back
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={handleSplitApplication}
          loading={isPending}
          size={ButtonSize.SM}
          disabled={applicationMeta.isLoading || isPending}
        >
          {isConfirmStep ? "Split application" : "Continue"}
        </Button>
      </div>
    </Modal>
  );
};
